import { useSearchParams } from "react-router-dom";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useListCrumb, usePageCrumbs } from "@/components/shell/breadcrumb";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { QueryErrorState } from "@/components/ui/States";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { filterUsers } from "@/components/users/userList";
import { UserEditPanel } from "@/components/users/UserEditPanel";
import { UserAccessPanel } from "@/components/users/UserAccessPanel";
import { useCreateUser, useUsers, type UserRow } from "@/api/hooks";
import { ExportButton } from "@/components/domain/ExportButton";
import { getLocalizedError } from "@/lib/api";
import { formatDate } from "@/domain/datetime";
import { ROLES, type Role } from "@hisense/shared";
import { translateStatus } from "@/domain/i18n";
import { slicePage, useClampListPage, useListQueryState } from "@/lib/listQuery";
import { useAuth } from "@/app/auth";

const ROLE_TONE: Record<Role, "brand" | "info" | "brass" | "neutral"> = {
  admin: "brand",
  manager: "info",
  accountant: "brass",
  viewer: "neutral",
};

/** Nhãn khu vực dữ liệu của một tài khoản; quản trị viên luôn thấy cả hai khu vực dù cột lưu gì. */
function regionLabel(u: UserRow): string {
  if (u.role === "admin" || u.dataRegion === "all") return "userAccess.regionAll";
  return u.dataRegion === "china" ? "userAccess.regionChina" : "userAccess.regionVietnam";
}

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { user: me } = useAuth();
  const list = useListQueryState();
  const [,setParams]=useSearchParams();
  const { data: users, isLoading, isFetching, error, refetch } = useUsers();
  const roleFilter=list.searchParams.get("role")??"";
  const statusFilter=list.searchParams.get("status")??"";
  const sort=list.searchParams.get("sort")??"newest";
  const filtered=filterUsers(users??[],list.q,roleFilter,statusFilter,sort);
  const sliced = slicePage(filtered, list.page, list.pageSize);
  const byName=(id:string|null)=>users?.find(u=>u.id===id)?.displayName || t("userWorkspace.system");
  const fullDate=(ms:number)=>new Date(ms).toLocaleString(i18n.language);
  useClampListPage(users ? sliced.total : undefined, list.clampPage);
  const createUser = useCreateUser();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [permitting, setPermitting] = useState<UserRow | null>(null);
  const [form, setForm] = useState({
    email: "",
    displayName: "",
    role: "viewer" as Role,
    password: "",
    // Client-side only: legacy never sent it and neither does userCreateSchema.
    confirmPassword: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const closeCreate=()=>{setOpen(false);setFormError(null);setForm({email:"",displayName:"",role:"viewer",password:"",confirmPassword:""});};
  const passwordMismatch = form.confirmPassword.length > 0 && form.password !== form.confirmPassword;
  usePageCrumbs(
    open
      ? [{ label: t("users.newUser") }]
      : editing
        ? [{ label: editing.displayName }]
        : [],
  );
  useListCrumb(useCallback(() => {
    setOpen(false);
    setEditing(null);
  }, []));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (form.password !== form.confirmPassword) {
      setFormError(t("users.passwordMismatch"));
      return;
    }
    try {
      // confirmPassword stays in the browser — userCreateSchema has no such field.
      await createUser.mutateAsync({
        email: form.email,
        displayName: form.displayName,
        role: form.role,
        password: form.password,
      });
      setForm({ email: "", displayName: "", role: "viewer", password: "", confirmPassword: "" });
      setOpen(false);
    } catch (err) {
      setFormError(getLocalizedError(err, t, "users.errorCreate"));
    }
  };

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <div className="flex shrink-0 flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1"><FieldLabel htmlFor="users-search">{t("common.search")}</FieldLabel><Input id="users-search" {...list.searchInput} placeholder={t("userWorkspace.search")}/></div>
        <div><FieldLabel htmlFor="users-role">{t("common.role")}</FieldLabel><Select id="users-role" value={roleFilter} onChange={e=>list.updateParam("role",e.target.value)}><option value="">{t("userWorkspace.allRoles")}</option>{ROLES.map(r=><option key={r} value={r}>{t(`roles.${r}`)}</option>)}</Select></div>
        <div><FieldLabel htmlFor="users-status">{t("common.status")}</FieldLabel><Select id="users-status" value={statusFilter} onChange={e=>list.updateParam("status",e.target.value)}><option value="">{t("userWorkspace.allStatuses")}</option>{["active","disabled"].map(r=><option key={r} value={r}>{translateStatus(t,r)}</option>)}</Select></div>
        <div><FieldLabel htmlFor="users-sort">{t("userWorkspace.sort")}</FieldLabel><Select id="users-sort" value={sort} onChange={e=>list.updateParam("sort",e.target.value)}>{["newest","name","email","lastLogin"].map(r=><option key={r} value={r}>{t(`userWorkspace.${r}`)}</option>)}</Select></div>
        <Button size="sm" variant="ghost" onClick={()=>setParams({})}>{t("userWorkspace.reset")}</Button>
        {/*
          Danh sách người dùng lọc và phân trang NGAY TRONG TRÌNH DUYỆT, không
          gọi lại máy chủ. Nên "toàn bộ đã lọc" cắt thẳng từ mảng đang có —
          vẫn đi qua đúng bộ nạp chung để hưởng các guard của nó.

          KHÔNG có cột mật khẩu, mã băm hay thuật toán băm. Tệp này ra khỏi hệ
          thống được, nên nó chỉ chứa những gì đã hiện trên bảng.
        */}
        <ExportButton<UserRow>
          name="users"
          title={t("nav.users")}
          rows={sliced.rows}
          total={filtered.length}
          disabled={isFetching}
          errorKey="users.errorLoad"
          fetchPage={(page, pageSize) =>
            Promise.resolve({
              data: filtered.slice((page - 1) * pageSize, page * pageSize),
              page,
              pageSize,
              total: filtered.length,
            })
          }
          columns={[
            { header: t("common.name"), value: (u) => u.displayName },
            { header: t("common.email"), value: (u) => u.email },
            { header: t("common.role"), value: (u) => t(`roles.${u.role}`) },
            { header: t("userAccess.region"), value: (u) => t(regionLabel(u)) },
            { header: t("common.status"), value: (u) => translateStatus(t, u.status) },
            { header: t("userWorkspace.lastLogin"), value: (u) => (u.lastLoginAt ? fullDate(u.lastLoginAt) : null) },
                        { header: t("common.createdOn"), value: (u) => fullDate(u.createdAt) },
            { header: t("common.createdBy"), value: (u) => byName(u.createdBy) },
            { header: t("userWorkspace.updated"), value: (u) => `${fullDate(u.updatedAt)} · ${byName(u.updatedBy)}` },
          ]}
        />
        <Button size="sm" onClick={() => { setEditing(null); closeCreate();setOpen(true); }}>
          <Plus className="size-4" />
          {t("users.newUser")}
        </Button>
      </div>

      {open && (
        <Modal
          title={t("users.createUser")}
          closeLabel={t("common.cancel")}
          onClose={() => { if(!createUser.isPending) closeCreate(); }}
          className="max-w-3xl"
        >
          <form onSubmit={submit} className="grid min-h-0 grid-cols-1 gap-3 overflow-y-auto p-5 sm:grid-cols-2 lg:grid-cols-3">
            <p className="text-xs text-ink-soft sm:col-span-2 lg:col-span-3">{t("userWorkspace.createHint")}</p>
            <div>
              <FieldLabel htmlFor="u-name">{t("common.name")}</FieldLabel>
              <Input id="u-name" value={form.displayName} required
                onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="u-email">{t("common.email")}</FieldLabel>
              <Input id="u-email" type="email" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="u-role">{t("common.role")}</FieldLabel>
              <Select
                id="u-role"
                containerClassName="w-full"
                className="capitalize"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{t(`roles.${r}`)}</option>
                ))}
              </Select>
            </div>
            <div>
              <FieldLabel htmlFor="u-pass">{t("common.password")}</FieldLabel>
              <Input id="u-pass" type="password" autoComplete="new-password" value={form.password}
                required minLength={8} maxLength={200}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div>
              <FieldLabel htmlFor="u-pass2">{t("users.confirmPassword")}</FieldLabel>
              <Input id="u-pass2" type="password" autoComplete="new-password" value={form.confirmPassword}
                required minLength={8} maxLength={200} invalid={passwordMismatch} aria-invalid={passwordMismatch}
                aria-describedby={passwordMismatch ? "u-pass-mismatch" : undefined}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} />
              {passwordMismatch && (
                <p id="u-pass-mismatch" className="mt-1 text-xs text-danger">{t("users.passwordMismatch")}</p>
              )}
            </div>
            {formError && (
              <p className="sm:col-span-2 lg:col-span-3 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">
                {formError}
              </p>
            )}
            <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
              <Button type="submit" size="sm" disabled={createUser.isPending || passwordMismatch}>
                {createUser.isPending ? t("common.creating") : t("users.createUser")}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={closeCreate}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        // key: remount on id change so one user's form state can never bleed
        // into another cached row (CLAUDE.md).
        <UserEditPanel key={editing.id} user={editing} onClose={() => setEditing(null)} />
      )}

      {permitting && (
        <UserAccessPanel key={permitting.id} user={permitting} onClose={() => setPermitting(null)} />
      )}

      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("users.errorLoad")}
        // 403 không phải "không tải được": dữ liệu vẫn ở đó, chỉ là vai trò này
        // không được xem — và nút Thử lại chỉ gọi lại đúng route đó để nhận
        // đúng 403 đó.
        errorNode={
          <QueryErrorState
            error={error}
            message={t("users.errorLoad")}
            forbiddenMessage={t("errors.forbidden")}
            onRetry={() => refetch()}
          />
        }
        page={list.page}
        pageSize={list.pageSize}
        total={sliced.total}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>{t("common.rowNumber")}</Th>
            <Th>{t("users.user")}</Th>
            <Th>{t("common.role")}</Th>
            <Th>{t("common.status")}</Th>
            <Th align="right">{t("common.lastLogin")}</Th>
            <Th>{t("userWorkspace.passwordState")}</Th>
            <Th align="right">{t("userWorkspace.created")}</Th>
            <Th align="right">{t("userWorkspace.updated")}</Th>
            <Th align="right" pinRight>
              <span className="sr-only">{t("users.editUser")}</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {sliced.rows.length === 0 && (
            <EmptyTableRow colSpan={9}>{t("common.noMatches")}</EmptyTableRow>
          )}
          {sliced.rows.map((u,index) => (
            <Tr key={u.id}>
              <Td className="font-mono tabular text-ink-faint">{(list.page-1)*list.pageSize+index+1}</Td>
              <Td>
                <div className="flex items-center gap-2.5">
                  <div>
                    <div className="font-medium text-ink">
                      {u.displayName}
                      {/* Đánh dấu chính mình: hai quy tắc khoá (không tự hạ
                          quyền, không tự vô hiệu hoá) chỉ dễ hiểu khi người
                          dùng nhìn ra dòng nào là của họ. */}
                      {u.id === me?.id && (
                        <span className="ml-1.5 rounded-sm bg-brand-tint px-1.5 py-0.5 text-2xs font-normal text-brand-ink">
                          {t("users.you")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-faint">{u.email}</div>
                  </div>
                </div>
              </Td>
              <Td>
                <Badge tone={ROLE_TONE[u.role]}>{t(`roles.${u.role}`)}</Badge>
                {/* Khu vực dữ liệu: chỉ hiện khi tài khoản bị giới hạn — admin và "cả hai khu vực" không có gì để báo. */}
                {u.role !== "admin" && u.dataRegion !== "all" ? (
                  <Badge tone="info" outline className="ml-1" title={t("userAccess.region")}>
                    {t(regionLabel(u))}
                  </Badge>
                ) : null}
              </Td>
              <Td>
                <Badge tone={u.status === "active" ? "ok" : "neutral"} outline className="capitalize">
                  {translateStatus(t, u.status)}
                </Badge>
              </Td>
              <Td align="right" className="font-mono tabular text-xs text-ink-soft">
                {u.lastLoginAt ? <span title={fullDate(u.lastLoginAt)}>{fullDate(u.lastLoginAt)}</span> : t("userWorkspace.never")}
              </Td>
              <Td><Badge tone={u.mustChangePassword ? "warn" : "neutral"} outline>{t(u.mustChangePassword ? "userWorkspace.temporary" : "userWorkspace.personal")}</Badge></Td>
              <Td align="right"><div className="font-mono tabular text-xs">{formatDate(u.createdAt)}</div><div className="text-xs text-ink-faint">{byName(u.createdBy)}</div></Td>
              <Td align="right"><div className="font-mono tabular text-xs">{fullDate(u.updatedAt)}</div><div className="text-xs text-ink-faint">{byName(u.updatedBy)}</div></Td>
              <Td align="right" pinRight>
                <div className="flex justify-end gap-1">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`${t("userAccess.open")} — ${u.displayName}`}
                    title={t("userAccess.open")}
                    onClick={() => { setOpen(false); setEditing(null); setPermitting(u); }}
                  >
                    <KeyRound className="size-3.5" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t("userWorkspace.edit",{name:u.displayName})}
                    title={t("users.editUser")}
                    onClick={() => { setOpen(false); setPermitting(null); setEditing(u); }}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
